/**
 * Report text for the Diagnostics panel. Everything here is pure (no DOM, no network) so the
 * format stays stable and testable: collectors in collect.ts produce `Section`s, formatReport
 * turns them into the text the owner copies and pastes to the developer.
 */
import type { LogEntry } from "./eventLog.ts";
import { clipLine, redactText } from "./redact.ts";

export const REPORT_FORMAT = "walk-player-diagnostics/1";

export interface Section {
  title: string;
  lines: string[];
  /** Set when the collector threw: the section prints the error instead of breaking the report. */
  error?: string;
}

/** Runs one collector; a throw, a rejection or no answer within `timeoutMs` becomes the section's error line. */
export async function runSection(title: string, collect: () => Promise<string[]> | string[], timeoutMs = 20_000): Promise<Section> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error(`no result after ${timeoutMs} ms`)), timeoutMs);
    });
    return { title, lines: await Promise.race([Promise.resolve().then(collect), timeout]) };
  } catch (err) {
    return { title, lines: [], error: errorText(err) };
  } finally {
    clearTimeout(timer);
  }
}

export function errorText(err: unknown): string {
  if (err instanceof Error) return clipLine(`${err.name}: ${err.message}`, 300);
  return clipLine(String(err), 300);
}

/** Response headers worth showing: where the answer came from and how it may be cached. */
export const SELECTED_HEADERS = [
  "server",
  "cache-control",
  "content-type",
  "etag",
  "date",
  "x-served-by",
  "via",
  "x-cache",
  "content-length",
] as const;

/** `server=nginx; cache-control=no-cache; ...` in a fixed order, only the headers present. */
export function pickHeaders(headers: { get(name: string): string | null }): string {
  const out: string[] = [];
  for (const name of SELECTED_HEADERS) {
    const value = headers.get(name);
    if (value !== null && value !== "") out.push(`${name}=${clipLine(value, 120)}`);
  }
  return out.length ? out.join("; ") : "(none of the selected headers)";
}

/** `key: value` rows with the values lined up. undefined/null values print as "n/a". */
export function kv(rows: [string, unknown][]): string[] {
  const width = Math.max(0, ...rows.map(([key]) => key.length));
  return rows.map(([key, value]) => `${key.padEnd(width)} : ${value === undefined || value === null ? "n/a" : String(value)}`);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return "n/a";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}

/** 5400 -> "5.4 s", 125000 -> "2 min 5 s", 7_300_000 -> "2 h 1 min". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "n/a";
  const sign = ms < 0 ? "-" : "";
  const abs = Math.abs(ms);
  if (abs < 1000) return `${sign}${Math.round(abs)} ms`;
  if (abs < 60_000) return `${sign}${(abs / 1000).toFixed(1)} s`;
  const minutes = Math.floor(abs / 60_000);
  if (abs < 3_600_000) return `${sign}${minutes} min ${Math.round((abs % 60_000) / 1000)} s`;
  return `${sign}${Math.floor(abs / 3_600_000)} h ${minutes % 60} min`;
}

/** UTC wall time of an event, `MM-DD HH:MM:SS.mmm`. */
export function formatTime(t: number): string {
  return Number.isFinite(t) ? new Date(t).toISOString().slice(5, 23).replace("T", " ") : "n/a";
}

/** "2026-10-18T10:00:00.000Z (3 min 2 s ago)". */
export function formatStamp(t: number | null | undefined, now: number): string {
  if (t === null || t === undefined || !Number.isFinite(t)) return "n/a";
  return `${new Date(t).toISOString()} (${formatDuration(now - t)} ago)`;
}

export interface ProbeResult {
  status: number;
  contentType: string | null;
  redirected: boolean;
  finalUrl: string;
  ms: number;
  /** First characters of a text body, for error answers. */
  snippet?: string;
}

/** One line per fetched URL. */
export function describeProbe(result: ProbeResult | { error: string; ms: number }, headers: string): string[] {
  if ("error" in result) return [`  FAILED after ${formatDuration(result.ms)}: ${result.error}`];
  const lines = [
    `  status ${result.status}${result.redirected ? " (redirected)" : ""}, ${formatDuration(result.ms)}, final ${redactText(result.finalUrl)}`,
    `  headers: ${headers}`,
  ];
  if (result.snippet) lines.push(`  body: ${clipLine(result.snippet, 100)}`);
  return lines;
}

/**
 * What a walk-path probe means. The hosting rewrites every unknown path to index.html (200, HTML):
 * a walk iframe that is NOT answered by the service worker would then show the Player's own page
 * (or an error) instead of the viewer.
 */
export function walkProbeNote(result: ProbeResult | { error: string }): string {
  if ("error" in result) return "no answer from the server (offline, or the connection failed)";
  if (result.status === 410) return "answered by the service worker: no open walk with that id (expected for the probe)";
  if (result.status === 404) return "plain 404: nothing at that path on the server";
  if (result.status === 200 && (result.contentType ?? "").includes("text/html")) {
    return "200 HTML: the server rewrites unknown paths to the app shell (index.html); if the service worker is not controlling the page a walk iframe would show the Player itself";
  }
  return "";
}

/** "this version is older/newer/equal to the deployed one". Versions are x.y.z; anything else is "unknown". */
export function compareVersions(running: string, deployed: string | null): string {
  if (!deployed) return "unknown (no deployed version could be read)";
  const parse = (v: string): number[] | null => {
    const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  };
  const a = parse(running);
  const b = parse(deployed);
  if (!a || !b) return "unknown (version not x.y.z)";
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return (a[i] as number) < (b[i] as number) ? `YES: running ${running}, server has ${deployed}` : `no: running ${running} is NEWER than the server's ${deployed}`;
  }
  return `no: running and deployed are both ${running}`;
}

/** What the ring buffer's lines look like in the report: grouped by session, previous ones marked. */
export function formatLog(entries: LogEntry[], currentSession: string, now: number): string[] {
  const order: string[] = [];
  const bySession = new Map<string, LogEntry[]>();
  for (const entry of entries) {
    if (!bySession.has(entry.sid)) {
      bySession.set(entry.sid, []);
      order.push(entry.sid);
    }
    bySession.get(entry.sid)?.push(entry);
  }
  if (!order.includes(currentSession)) order.push(currentSession);
  const lines: string[] = ["times are UTC; the last entry of a previous session is roughly when it ended"];
  for (const sid of order) {
    const list = bySession.get(sid) ?? [];
    const current = sid === currentSession;
    const first = list[0];
    const last = list[list.length - 1];
    lines.push(
      `--- ${current ? "this session" : "previous session"} ${sid} (${list.length} entries` +
        (first && last ? `, ${formatTime(first.t)} .. ${formatTime(last.t)}` : "") +
        ") ---",
    );
    for (const entry of list) lines.push(`${formatTime(entry.t)} ${entry.kind.padEnd(12)} ${entry.msg}`);
    if (!current && last) {
      const sawPagehide = list.some((e) => e.kind === "pagehide");
      lines.push(
        `    ended ${formatDuration(now - last.t)} ago; pagehide recorded: ${sawPagehide ? "yes (closed or navigated away)" : "NO (no goodbye: killed, crashed or out of memory?)"}`,
      );
    }
  }
  return lines;
}

export interface ReportHeader {
  generatedAt: number;
  version: string;
}

/** The whole text. Stable layout: header, then `== N. Title ==` blocks in the order given. */
export function formatReport(header: ReportHeader, sections: Section[]): string {
  const out: string[] = [
    "WALK PLAYER DIAGNOSTICS",
    `format    : ${REPORT_FORMAT}`,
    `generated : ${new Date(header.generatedAt).toISOString()}`,
    `app       : v${header.version}`,
  ];
  sections.forEach((section, index) => {
    out.push("", `== ${index + 1}. ${section.title} ==`);
    if (section.error) out.push(`!! this section failed: ${section.error}`);
    out.push(...section.lines);
  });
  out.push("", "== end of report ==");
  return out.join("\n");
}
